import { ObjectListRequestType, ObjectListResponseType } from '../ObjectListResponseTypes'

// Hybrid interface: Satisfies ObjectListResponseType root properties for old format,
// while making the new nested format structure optional.
interface InvoiceListResponse extends ObjectListResponseType {
  // Satisfies/Extends ObjectListResponseType (Root level for old format)
  total: number
  page: number
  invoices?: InvoiceResponse[] // Present in old format

  // Present in new format
  data?: {
    total: number
    page: number
    pageSize: number
    invoices: InvoiceResponse[]
  }
  meta?: {
    DetailDownloadFormats: string[]
  }
}

interface InvoiceResponse {
  id: number
  invoiceNr: string | number // Handles string (old) or number (new)
  name: string
  issueDate: string
  dueDate: string
  cycleStartDate?: string
  cycleEndDate?: string
  type: string
  totalAmount: InvoiceResponseAmount
  remainingAmount: InvoiceResponseAmount
  status: string
  cdrAvailable?: number
}

interface InvoiceResponseAmount {
  beforeTax: number
  total: number
}

// Request payloads
interface InvoiceRequestBody extends ObjectListRequestType {}
interface InvoiceCustomerRequestBody {
  emailAddressInvoice: string
  invoiceByPost: 1
  street: string
  streetNumber: string
  bus: string
  zipCode: string
  city: string
  countryID: number
}

// Guard: Validates that the payload is either the old format or the new nested format
const isInvoiceListResponse = (response: any): response is InvoiceListResponse => {
  if (!response || typeof response !== 'object') {
    return false
  }

  const isOldFormat = 'invoices' in response && Array.isArray(response.invoices)

  const isNewFormat =
    'data' in response &&
    response.data !== null &&
    typeof response.data === 'object' &&
    Array.isArray(response.data.invoices)

  return isOldFormat || isNewFormat
}

const isInvoiceResponse = (response: any): response is InvoiceResponse => {
  if (!response || typeof response !== 'object') {
    return false
  }
  const target = Array.isArray(response) ? response[0] : response
  return target && typeof target === 'object' && 'issueDate' in target
}

const isInvoiceRequestBody = (request: any): request is InvoiceRequestBody => {
  return (
    request !== undefined &&
    request !== null &&
    typeof request.pageSize === 'number' &&
    typeof request.page === 'number'
  )
}

const isInvoiceCustomerRequestBody = (request: any): request is InvoiceCustomerRequestBody => {
  return request !== undefined && request !== null && 'emailAddressInvoice' in request
}

export type { InvoiceResponse, InvoiceRequestBody, InvoiceListResponse, InvoiceCustomerRequestBody }
export { isInvoiceResponse, isInvoiceRequestBody, isInvoiceListResponse, isInvoiceCustomerRequestBody }
